import { useTranslation } from "react-i18next"

import { ApiError } from "@/api/client/_shared"
import { ArrowLeft, FolderLock } from "@/components/icons"
import { Button } from "@/components/ui/button"

type FilePickerErrorStateProps = {
  error: Error
  onBack?: () => void
}

/**
 * Shown in place of a file list when the query behind it failed.
 *
 * A SharePoint site the user cannot read used to surface as the generic
 * "Oops! Something went wrong" (or as an empty list), which reads as a broken
 * picker rather than a permissions question (#2874). The backend now answers
 * such cases with the upstream 403/404, so the message can be specific.
 */
function messageFor(status: number | undefined): { key: string; defaultValue: string } {
  if (status === 403) {
    return {
      key: "filepicker.graph_access_denied",
      defaultValue: "You don't have access to this location.",
    }
  }
  if (status === 404) {
    return {
      key: "filepicker.graph_not_found",
      defaultValue: "This location is no longer available.",
    }
  }
  return {
    key: "filepicker.load_error",
    defaultValue: "The files could not be loaded. Please try again.",
  }
}

export function FilePickerErrorState({ error, onBack }: FilePickerErrorStateProps) {
  const { t } = useTranslation()
  const status = error instanceof ApiError ? error.status : undefined
  const { key, defaultValue } = messageFor(status)
  const message = t(key, { defaultValue })

  return (
    <div
      className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-3 px-6 text-center"
      role="alert"
    >
      <FolderLock aria-hidden="true" className="h-6 w-6 sm:h-8 sm:w-8" />
      <p className="text-sm font-medium">{message}</p>
      {onBack && (
        <Button size="sm" type="button" variant="outline" onClick={onBack}>
          <ArrowLeft aria-hidden="true" className="mr-1 h-4 w-4" />
          {t("filepicker.back", { defaultValue: "Back" })}
        </Button>
      )}
    </div>
  )
}
